import { IconButton, MenuItem, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Modal from '@mui/material/Modal';
import Select from '@mui/material/Select';
import { useState } from 'react';
import { useDispatch } from 'react-redux-latest';
import { useNavigate } from 'react-router-dom';
import { handleSuccessNotification, resetImportProspects, runBulkImport } from '../../redux/actions';
import { AppDispatch } from '../../redux/store/configureStore';
import CloseIcon from '../icons/close';
import { MoveProspectsToCampaignContainer } from './upload-prospect-to-prospect-collection.styled';
import { useProspectCollections } from '../../hooks/use-prospect-collections';
import { paths } from '../../routes/paths';
import { useMsal } from '@azure/msal-react';

type Props = {
    onModalClose: () => void;
    campaignId?: string;
};

export function UploadProspectToProspectCollectionModal({ onModalClose }: Props) {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { prospectCollections } = useProspectCollections();
    const [loading, setLoading] = useState(false);
    const [disabled, setDisabled] = useState(true);
    const msal = useMsal();

    const [targetProspectCollectionId, setTargetProspectCollectionId] = useState('');

    const handleClose = () => {
        setTargetProspectCollectionId('');
        onModalClose();
    };

    const handleChange = (event) => {
        setDisabled(false);
        setTargetProspectCollectionId(event.target.value);
    };

    const purchaseProspectsToOtherCampaign = async () => {
        setLoading(true);
        const prospectsSuccescfullyImported = await dispatch(runBulkImport(msal.instance, targetProspectCollectionId));
        setLoading(false);
        if (prospectsSuccescfullyImported) {
            dispatch(handleSuccessNotification('Successfully uploaded prospects', 7000));
            dispatch(resetImportProspects());
            navigate(paths.PROSPECT_COLLECTIONS);
        }
        handleClose();
    };

    return (
        <>
            <Modal open onClose={handleClose} onClick={(event) => event.stopPropagation()}>
                <MoveProspectsToCampaignContainer>
                    <div className="modal-container">
                        <div className="input-container">
                            <div>
                                <IconButton
                                    onClick={handleClose}
                                    disableRipple
                                    className="close-icon"
                                    data-Cy="close-icon"
                                    size="large"
                                >
                                    <CloseIcon />
                                </IconButton>
                                <Typography variant="h3" className="title margin-bottom">
                                    Add to collection
                                </Typography>
                            </div>
                            <div>
                                <Typography className="typography title-small">Select prospect collection</Typography>
                                <FormControl className="form-control" variant="filled">
                                    <Select
                                        value={targetProspectCollectionId}
                                        onChange={handleChange}
                                        displayEmpty
                                        disableUnderline
                                        className={targetProspectCollectionId === '' ? 'disabled' : 'active'}
                                        MenuProps={{ style: { maxWidth: '1px' } }} // kinda hacky, but it works for line MenuItem span
                                        data-cy="select-campaign-dropdown"
                                    >
                                        <MenuItem value="" disabled>
                                            Select...
                                        </MenuItem>
                                        {prospectCollections &&
                                            prospectCollections.map(({ name, id }) => (
                                                <MenuItem key={id} value={id} data-id={id} id={id} className="option">
                                                    <span
                                                        style={{
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {name}
                                                    </span>
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <Divider className="divider" />
                        <div className="buttons-container">
                            <Button
                                className="button button-outlined-blue "
                                variant="outlined"
                                data-cy="close-button"
                                onClick={handleClose}
                            >
                                Close
                            </Button>
                            <Button
                                className={`${disabled ? 'disabled' : 'button-blue'} button`}
                                variant="contained"
                                endIcon={loading && <CircularProgress color="inherit" size={20} />}
                                onClick={() => purchaseProspectsToOtherCampaign()}
                                data-cy="move-button"
                            >
                                {'Upload'}
                            </Button>
                        </div>
                    </div>
                </MoveProspectsToCampaignContainer>
            </Modal>
        </>
    );
}
